import { msg } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { createFileRoute, Link } from "@tanstack/react-router";

import { parseSEOMetaLoaderData, seo } from "@/lib/seo";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import AnimatedText from "@/components/animated-text";
import Hero, { HeroTitle } from "@/components/hero";
import HowItWorks from "@/components/list-your-property/how-it-works";
import ListingBenefits from "@/components/list-your-property/listing-benefits";

import EllipseIcon from "@/assets/icons/ellipse.svg?react";
import LogoIcon from "@/assets/icons/logo.svg?react";
import exceptionalStays from "@/assets/images/exceptional-stays.png";
import heroBg from "@/assets/images/list-property-hero.jpeg";

export const Route = createFileRoute("/_default-layout/list-property/")({
  loader: ({ context: { i18n }, location }) => {
    return {
      seoMeta: seo({
        i18n,
        path: location.pathname,
        title: msg`List Your Property`,
        description: msg`List your property with El Safwa and unlock its full potential. We list your property on multiple platforms with exceptional service and property management.`,
        keywords:
          "list property, property listing, El Safwa property management, property rental, multiple platforms, property potential",
      }),
    };
  },
  head: ({ loaderData }) => ({
    meta: [
      ...parseSEOMetaLoaderData(loaderData, {
        title: "List Your Property",
      }),
    ],
  }),
  component: RouteComponent,
});

function RouteComponent() {
  return (
    <div>
      <Hero imageBg={heroBg}>
        <HeroTitle linkTo="/list-property" hashID="listing-benefits">
          <Trans>Unlock Your</Trans>
          <br />
          <span className="font-bold">
            <Trans>Property's Potential</Trans>
          </span>
        </HeroTitle>
        <ListPropertyCTA className="mt-4 md:hidden" />
      </Hero>
      <div id="listing-benefits" className="container pt-12 pb-6 lg:pt-[120px] lg:pb-[60px]">
        <ListingBenefits />
      </div>
      <div className="py-6 lg:px-8 lg:py-[60px]">
        <HowItWorks />
      </div>
    </div>
  );
}

interface ListPropertyCTAProps {
  className?: string;
}
function ListPropertyCTA({ className }: ListPropertyCTAProps) {
  return (
    <section
      className={cn(
        "text-foreground mb-5 self-end rounded-4xl bg-(--color-bg-color-bg-beige) p-4 md:p-5",
        className,
      )}
    >
      <div className="mb-6 flex justify-between gap-4">
        <div className="flex flex-col justify-between gap-2">
          <LogoIcon className="size-12" />
          <h3 className="title-desktop-small-titlessb max-w-[200px] capitalize">
            <Trans>Handled by experts</Trans>
          </h3>
        </div>
        <img
          src={exceptionalStays}
          alt="Table"
          className="size-[124px] rounded-2xl object-cover object-center"
        />
      </div>
      <p className="mb-6 flex items-center gap-1 font-medium">
        <Trans>
          El Safwa makes short-term rental management effortless, helping you maximize income. Start
          your journey with us today!
        </Trans>
      </p>
      <Button size="lg" className="w-full" asChild>
        <Link to="/list-property/submit-property">
          <EllipseIcon className="size-2" />
          <AnimatedText>
            <Trans>List Your Property</Trans>
          </AnimatedText>
        </Link>
      </Button>
    </section>
  );
}
